import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { getAllClassrooms } from "../../api/sponteAPI";
import { getSubjects } from "../../api/sponteAPI";
import { createPlan } from "../../api/endpoints/plans";
import { getErrorMessage } from "../../utils/errorHandler";
import { anoLetivo } from "../../utils/YEARCONSTS";
import { getTeachersAllocations } from "../../api/autenticoAPI";
// Models não são necessários aqui, usamos dados do Sponte diretamente

const NewPlanForm = ({ onClose, onSuccess, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]); // Múltiplas turmas
  const [selectedSubject, setSelectedSubject] = useState(null); // Disciplina selecionada
  const [teacherAllocations, setTeacherAllocations] = useState([]);

  const [formData, setFormData] = useState({
    subjectId: "",
    gradeId: "",
    planType: "DAILY",
    weekStart: "",
    weekEnd: "",
    date: "",
    unit: "",
    classActivities: "",
    homework: "",
    resources: "",
  });


  // Carregar alocações do professor se for professor
  useEffect(() => {
    const loadTeacherAllocations = async () => {
      if (!user || user.level !== "teacher" || !user.id) {
        setTeacherAllocations([]);
        return;
      }

      try {
        const allocations = await getTeachersAllocations(user.id, null, null);
        
        // Normalizar alocações - o backend pode retornar agrupado ou não agrupado
        let normalized = [];
        if (Array.isArray(allocations)) {
          allocations.forEach(a => {
            if (a.Disciplinas && Array.isArray(a.Disciplinas)) {
              // Formato agrupado: { TurmaID, turmaName, Disciplinas: [{ DisciplinaID, disciplinaName }] }
              a.Disciplinas.forEach(disciplina => {
                normalized.push({
                  TurmaID: typeof a.TurmaID === 'number' ? a.TurmaID : parseInt(a.TurmaID || a.turmaID, 10),
                  turmaName: a.turmaName || a.turma_name,
                  DisciplinaID: typeof disciplina.DisciplinaID === 'number' ? disciplina.DisciplinaID : parseInt(disciplina.DisciplinaID || disciplina.disciplinaID, 10),
                  disciplinaName: disciplina.disciplinaName || disciplina.disciplina_name,
                });
              });
            } else {
              // Formato não agrupado
              normalized.push({
                TurmaID: typeof a.TurmaID === 'number' ? a.TurmaID : parseInt(a.TurmaID || a.turmaID, 10),
                turmaName: a.turmaName || a.turma_name,
                DisciplinaID: typeof a.DisciplinaID === 'number' ? a.DisciplinaID : parseInt(a.DisciplinaID || a.disciplinaID, 10),
                disciplinaName: a.disciplinaName || a.disciplina_name,
              });
            }
          });
        }
        
        setTeacherAllocations(normalized);
      } catch (error) {
        console.error("Erro ao carregar alocações do professor:", error);
        setTeacherAllocations([]);
      }
    };

    loadTeacherAllocations();
  }, [user]);

  // Carregar turmas disponíveis
  useEffect(() => {
    const loadClassrooms = async () => {
      try {
        setIsLoading(true);
        const allClassrooms = await getAllClassrooms(anoLetivo);
        
        // Normalizar turmas - a API Sponte pode retornar arrays dentro dos objetos
        const normalizedClassrooms = (allClassrooms || []).map(classroom => {
          // Normalizar TurmaID
          let turmaID = classroom.TurmaID;
          if (Array.isArray(turmaID)) {
            turmaID = turmaID[0];
          }
          turmaID = typeof turmaID === 'number' ? turmaID : parseInt(turmaID || classroom.id, 10);
          
          // Normalizar Nome
          let nome = classroom.Nome;
          if (Array.isArray(nome)) {
            nome = nome[0];
          }
          nome = nome || classroom.name || '';
          
          return {
            ...classroom,
            TurmaID: turmaID,
            Nome: nome,
            id: turmaID, // Garantir que id também seja o TurmaID normalizado
          };
        });
        
        // Se for professor e tiver alocações, filtrar apenas as turmas alocadas
        if (user && user.level === "teacher" && teacherAllocations.length > 0) {
          // Extrair TurmaIDs únicos das alocações
          const allocatedTurmaIds = new Set(
            teacherAllocations.map(a => {
              const turmaId = typeof a.TurmaID === 'number' ? a.TurmaID : parseInt(a.TurmaID, 10);
              if (isNaN(turmaId)) {
                return null;
              }
              return turmaId;
            }).filter(id => id !== null)
          );
          
          const filtered = normalizedClassrooms.filter(c => {
            const cTurmaID = typeof c.TurmaID === 'number' ? c.TurmaID : parseInt(c.TurmaID || c.id, 10);
            return allocatedTurmaIds.has(cTurmaID);
          });
          
          setClassrooms(filtered);
        } else if (user && user.level === "teacher" && teacherAllocations.length === 0) {
          // Professor sem alocações - não mostrar nenhuma turma
          setClassrooms([]);
        } else {
          // Admin, coordenador, etc. - mostrar todas as turmas
          setClassrooms(normalizedClassrooms);
        }
      } catch (error) {
        console.error("Erro ao carregar turmas:", error);
        setError("Erro ao carregar turmas. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    };

    loadClassrooms();
  }, [user, teacherAllocations]);

  // Carregar disciplinas disponíveis para o professor (baseado nas alocações)
  useEffect(() => {
    const loadAvailableSubjects = async () => {
      if (user && user.level === "teacher" && teacherAllocations.length > 0) {
        // Para professores, mostrar apenas disciplinas das alocações
        const uniqueSubjects = new Map();
        teacherAllocations.forEach(a => {
          const discId = typeof a.DisciplinaID === 'number' ? a.DisciplinaID : parseInt(a.DisciplinaID, 10);
          const discName = a.disciplinaName || '';
          if (discId && !uniqueSubjects.has(discId)) {
            uniqueSubjects.set(discId, {
              id: discId,
              name: discName,
            });
          }
        });
        setSubjects(Array.from(uniqueSubjects.values()));
      } else if (user && user.level !== "teacher") {
        // Para admin/coordenador, carregar todas as disciplinas da primeira turma (se houver)
        if (classrooms.length > 0) {
          try {
            const firstTurmaId = classrooms[0].TurmaID || classrooms[0].id;
            const data = await getSubjects(firstTurmaId);
            const normalized = (data || []).map((subj) => ({
              id: subj.DisciplinaID || subj.id,
              name: subj.Nome || subj.name,
            }));
            setSubjects(normalized);
          } catch (error) {
            console.error("Erro ao carregar disciplinas:", error);
          }
        }
      } else {
        setSubjects([]);
      }
    };

    loadAvailableSubjects();
  }, [user, teacherAllocations, classrooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedClassrooms.length === 0) {
      setError("Por favor, selecione pelo menos uma turma.");
      return;
    }

    if (!formData.subjectId || !formData.unit || !formData.classActivities || !formData.homework) {
      setError("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    // Verificar se todas as turmas selecionadas têm a disciplina selecionada nas alocações
    if (user && user.level === "teacher" && teacherAllocations.length > 0) {
      const subjectId = parseInt(formData.subjectId);
      const invalidTurmas = selectedClassrooms.filter(classroom => {
        const turmaId = typeof classroom === "object" 
          ? (classroom.TurmaID || classroom.id)
          : parseInt(classroom, 10);
        
        const hasAllocation = teacherAllocations.some(a => {
          const aTurmaID = typeof a.TurmaID === 'number' ? a.TurmaID : parseInt(a.TurmaID, 10);
          const aDisciplinaID = typeof a.DisciplinaID === 'number' ? a.DisciplinaID : parseInt(a.DisciplinaID, 10);
          return aTurmaID === turmaId && aDisciplinaID === subjectId;
        });
        
        return !hasAllocation;
      });

      if (invalidTurmas.length > 0) {
        setError("Todas as turmas selecionadas devem ter a disciplina selecionada nas suas alocações.");
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      // Criar um planejamento para cada turma selecionada
      const promises = selectedClassrooms.map(async (classroom) => {
        const turmaId = typeof classroom === "object" 
          ? (classroom.TurmaID || classroom.id)
          : parseInt(classroom, 10);
        
        const planData = {
          subjectId: parseInt(formData.subjectId),
          gradeId: turmaId, // TurmaID do Sponte, backend busca o id correspondente
          planType: formData.planType,
          weekStart: formData.planType === "WEEKLY" ? formData.weekStart : null,
          weekEnd: formData.planType === "WEEKLY" ? formData.weekEnd : null,
          date: formData.planType === "DAILY" ? formData.date : null,
          unit: formData.unit,
          classActivities: formData.classActivities,
          homework: formData.homework,
          resources: formData.resources || null,
        };

        return createPlan(planData);
      });

      await Promise.all(promises);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "planType"
        ? {
            weekStart: "",
            weekEnd: "",
            date: "",
          }
        : {}),
    }));
  };

  // Filtrar turmas disponíveis baseado na disciplina selecionada
  const getAvailableClassrooms = () => {
    if (!formData.subjectId) {
      return classrooms;
    }

    const subjectId = parseInt(formData.subjectId);
    
    // Se for professor, filtrar apenas turmas que têm essa disciplina nas alocações
    if (user && user.level === "teacher" && teacherAllocations.length > 0) {
      return classrooms.filter(classroom => {
        const turmaId = typeof classroom.TurmaID === 'number' ? classroom.TurmaID : parseInt(classroom.TurmaID || classroom.id, 10);
        return teacherAllocations.some(a => {
          const aTurmaID = typeof a.TurmaID === 'number' ? a.TurmaID : parseInt(a.TurmaID, 10);
          const aDisciplinaID = typeof a.DisciplinaID === 'number' ? a.DisciplinaID : parseInt(a.DisciplinaID, 10);
          return aTurmaID === turmaId && aDisciplinaID === subjectId;
        });
      });
    }

    return classrooms;
  };

  const handleClassroomToggle = (classroom) => {
    const turmaId = classroom.TurmaID || classroom.id;
    setSelectedClassrooms((prev) => {
      const alreadySelected = prev.some((c) => (c.TurmaID || c.id) === turmaId);
      if (alreadySelected) {
        return prev.filter((c) => (c.TurmaID || c.id) !== turmaId);
      }
      return [...prev, classroom];
    });
  };

  const isClassroomSelected = (turmaId) =>
    selectedClassrooms.some((c) => (c.TurmaID || c.id) === turmaId);

  const handleSubjectChange = (e) => {
    const subjectId = e.target.value;
    setFormData(prev => ({ ...prev, subjectId }));
    setSelectedSubject(subjects.find(s => s.id === parseInt(subjectId, 10)));
    // Limpar turmas selecionadas quando mudar a disciplina
    setSelectedClassrooms([]);
  };

  // handleGradeToggle removido - não utilizado

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "900px",
          width: "90%",
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600", color: "#1e958c" }}>
            Novo Planejamento
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "4px",
              marginBottom: "16px",
              color: "#c33",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Disciplina */}
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Disciplina *
            </label>
            <select
              name="subjectId"
              value={formData.subjectId}
              onChange={handleSubjectChange}
              disabled={subjects.length === 0}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
                opacity: subjects.length === 0 ? 0.6 : 1,
              }}
              required
            >
              <option value="">Selecione uma disciplina</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          {/* Turmas - seleção com clique (checkbox) */}
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Turmas * (clique para selecionar/desmarcar)
            </label>
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "10px",
                maxHeight: "190px",
                overflowY: "auto",
                background: "#fafafa",
                opacity: !formData.subjectId || getAvailableClassrooms().length === 0 ? 0.6 : 1,
              }}
            >
              {formData.subjectId && getAvailableClassrooms().length === 0 && (
                <div style={{ fontSize: "13px", color: "#666" }}>
                  Nenhuma turma disponível para esta disciplina.
                </div>
              )}
              {getAvailableClassrooms().map((classroom) => {
                const turmaId = classroom.TurmaID || classroom.id;
                const turmaName = String(classroom.Nome || classroom.name || "");
                const checked = isClassroomSelected(turmaId);
                return (
                  <label
                    key={turmaId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 8px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: checked ? "#e0f2fe" : "transparent",
                      border: checked ? "1px solid #bae6fd" : "1px solid transparent",
                      marginBottom: "6px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleClassroomToggle(classroom)}
                      style={{ cursor: "pointer" }}
                    />
                    <span style={{ fontSize: "14px" }}>{turmaName}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#666" }}>
              {formData.subjectId
                ? `Selecione uma ou mais turmas que têm a disciplina "${selectedSubject?.name || ""}".`
                : "Selecione uma disciplina primeiro."}
            </div>
            {selectedClassrooms.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                  Turmas selecionadas ({selectedClassrooms.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {selectedClassrooms.map((classroom, index) => {
                    const turmaId = classroom.TurmaID || classroom.id;
                    const turmaName = String(classroom.Nome || classroom.name || "");
                    return (
                      <span
                        key={turmaId || index}
                        style={{
                          backgroundColor: "#e0f2fe",
                          color: "#0369a1",
                          borderRadius: "12px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          border: "1px solid #bae6fd",
                        }}
                      >
                        {turmaName}
                      </span>
                    );
                  })}
                  <span style={{ fontSize: "12px", color: "#0369a1", backgroundColor: "#e0f2fe", padding: "6px 10px", borderRadius: "12px", border: "1px solid #bae6fd" }}>
                    Será criado {selectedClassrooms.length} planejamento{selectedClassrooms.length > 1 ? "s" : ""} (um por turma)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Linha: Tipo de planejamento e data/semana */}
          {formData.planType === "WEEKLY" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
                  Tipo de Planejamento *
                </label>
                <select
                  name="planType"
                  value={formData.planType}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                  }}
                  required
                >
                  <option value="DAILY">Diário</option>
                  <option value="WEEKLY">Semanal</option>
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
                    Início da Semana *
                  </label>
                  <input
                    type="date"
                    name="weekStart"
                    value={formData.weekStart}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      fontSize: "14px",
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
                    Fim da Semana *
                  </label>
                  <input
                    type="date"
                    name="weekEnd"
                    value={formData.weekEnd}
                    onChange={handleChange}
                    min={formData.weekStart}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      fontSize: "14px",
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
                  Tipo de Planejamento *
                </label>
                <select
                  name="planType"
                  value={formData.planType}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                  }}
                  required
                >
                  <option value="DAILY">Diário</option>
                  <option value="WEEKLY">Semanal</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
                  Data *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                  }}
                  required
                />
              </div>
            </div>
          )}

          {/* Conteúdo/Unidade */}
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Conteúdo/Unidade *
            </label>
            <textarea
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              rows={5}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                resize: "vertical",
              }}
              required
              placeholder="Digite o conteúdo da unidade..."
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Atividades em Sala *
            </label>
            <textarea
              name="classActivities"
              value={formData.classActivities}
              onChange={handleChange}
              rows={4}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                resize: "vertical",
              }}
              required
              placeholder="Descreva as atividades em sala..."
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Atividades para Casa *
            </label>
            <textarea
              name="homework"
              value={formData.homework}
              onChange={handleChange}
              rows={4}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                resize: "vertical",
              }}
              required
              placeholder="Descreva as atividades para casa..."
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "500" }}>
              Recursos Utilizados (opcional)
            </label>
            <textarea
              name="resources"
              value={formData.resources}
              onChange={handleChange}
              rows={3}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                resize: "vertical",
              }}
              placeholder="Liste os recursos utilizados..."
            />
          </div>

          {/* Botões */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "12px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: "10px 20px",
                backgroundColor: isLoading ? "#9ca3af" : "#1e958c",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {isLoading ? "Criando..." : "Criar Planejamento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPlanForm;

